import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  Optional,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BllExpandComponent } from './bll-expand.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { bllExpansionAnimations } from './expansion-animations';
/** Used to generate unique ID for each accordion item. */
let nextId = 0;

/** MatExpansionPanel's states. */
export type BllExpansionPanelState = 'expanded' | 'collapsed';

@Component({
  selector: 'bll-expand-panel',
  templateUrl: './bll-expand-panel.component.html',
  host: {
    class: 'expand-item',
    '[class.open]': 'isOpen',
    '[class.disabled]': '!noDisabledClass && disabled',
  },
  animations: [bllExpansionAnimations.bodyExpansion],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@Input()
export class BllExpandPanelComponent implements OnInit, OnDestroy {
  _isOpen: boolean = false;
  _firstOpened: boolean = false;

  @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter();
  @Output() firstOpen: EventEmitter<boolean> = new EventEmitter();
  @Output() afterExpand = new EventEmitter();
  @Output() afterCollapsed = new EventEmitter();
  @Input()
  get isOpen(): boolean {
    return this._isOpen;
  }
  set isOpen(value: boolean) {
    if (value !== this._isOpen) {
      if (value && this.expand) {
        this.expand.closeOtherPanels(this);
      }

      this._isOpen = value;
      if (this._firstOpened === false) {
        this._firstOpened = true;
        this.firstOpen.emit(true);
      }
      this.isOpenChange.emit(value);
    }
  }

  @Input()
  set noDisabledClass(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this._noDisabledClass) {
      this._noDisabledClass = newValue;
    }
  }
  get noDisabledClass() {
    return this._noDisabledClass || this.expand.noDisabledClass;
  }
  private _noDisabledClass: any;
  /**
   * Whether the accordion panel is disabled.
   */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.disabled) {
      this._disabled = newValue;
    }
  }
  private _disabled: boolean = false;

  constructor(@Optional() protected expand: BllExpandComponent) {}

  readonly headerId: string = `bll-accordion-header-${nextId++}`;
  readonly itemId: string = `bll-accordion-item-${nextId}`;

  ngOnInit(): void {
    if (this.expand) {
      this.expand.addPanel(this);
    }
  }

  ngOnDestroy(): void {
    if (this.expand) {
      this.expand.removePanel(this);
    }
  }

  toggle(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
    }
  }

  /** Gets the expanded state string. */
  _getExpandedState(): BllExpansionPanelState {
    return this.isOpen ? 'expanded' : 'collapsed';
  }

  _bodyAnimationDone(event) {
    if (event.fromState !== 'void') {
      if (event.toState === 'expanded') {
        this.afterExpand.emit();
      } else if (event.toState === 'collapsed') {
        this.afterCollapsed.emit();
      }
    }
  }
}
